import { forwardRef, useEffect, useState } from 'react';
import Gravatar from 'react-awesome-gravatar';
import { Badge, Dropdown, Image } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import { useFirebase } from '../../../contexts/FirebaseContext';
import { useGaming } from '../../../contexts/GamingContext';
import { useCapacitor } from '../../../hooks/useCapacitor';
import { useCurrentLanguage } from '../../../hooks/useCurrentLanguage';
import { generateColorFromArbitraryString } from '../../utils/utils';
import { BetaBadge } from '../BetaBadge/BetaBadge';
import { Feedback } from '../Feedback/Feedback';
import styles from './ProfileCircle.module.scss';

const CommonChild = ({ label, beta = false, append = null }) => (
  <div className={'d-flex justify-content-between align-items-center'}>
    <span>
      {label} {append} {beta && <BetaBadge />}
    </span>
  </div>
);

const NestedDropdownToggle = forwardRef(({ children, onClick }: any, ref) => (
  <a
    className={'dropdown-item'}
    href={''}
    ref={ref as any}
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      onClick(e);
    }}
  >
    <CommonChild label={children} />
  </a>
));

const DropdownItemLink = ({ label, to = null, onClick = null, beta = false, append = null }) => {
  if (to) {
    return (
      <Dropdown.Item as={Link} to={to}>
        <CommonChild label={label} beta={beta} append={append} />
      </Dropdown.Item>
    );
  }

  return (
    <Dropdown.Item onClick={onClick}>
      <CommonChild label={label} beta={beta} append={append} />
    </Dropdown.Item>
  );
};

const ProfileCircleComponent = ({ up = false }) => {
  const { currentUser, userDataFmt, logout, staticData, isLoggedIn, subscription, userClaims } = useFirebase();
  const { player, isEnabled: isGamingEnabled } = useGaming();
  const [isAdmin, setIsAdmin] = useState(false);
  const [openAdminMenu, setOpenAdminMenu] = useState<string | null>(null);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const history = useHistory();
  const { isNative } = useCapacitor();
  const { t } = useTranslation();
  const { currentLanguage } = useCurrentLanguage();

  const handleLogout = async () => {
    history.push('/?logout=1');
    await logout();
    window.location = window.location.pathname as any;
  };

  const handleFeedback = () => {
    setFeedbackModalOpen(true);
  };

  useEffect(() => {
    setIsAdmin(!!currentUser && !!userDataFmt?.admin);
  }, [currentUser, userDataFmt]);

  return (
    <>
      <Dropdown
        drop={up ? 'up' : 'down'}
        onToggle={(isOpen) => {
          if (!isOpen) setOpenAdminMenu(null);
        }}
      >
        <Dropdown.Toggle as={GravatarDropdownToggle}>
          {{
            player,
            user: currentUser ?? {},
            ENABLE_GAMING: isGamingEnabled,
          }}
        </Dropdown.Toggle>
        <Dropdown.Menu align={'right'} className="tootor-dropdown-menu profile-dropdown-menu">
          <Dropdown.Header>{currentUser?.email ?? ''}</Dropdown.Header>
          <DropdownItemLink to={'/profile'} label={t('labels.profilo')} />

          {!isNative && staticData.ENABLE_GIFTS && <DropdownItemLink to={'/gift'} label={t('labels.regalaAbbonamento')} />}
          {!!staticData.SHOW_TV_LOGIN_BUTTON && <DropdownItemLink to={'/tv'} label={t('labels.accediTV')} />}
          {isAdmin && (
            <>
              <Dropdown.Divider />
              <Dropdown.Header>Admin tools</Dropdown.Header>

              <Dropdown drop={'left'} show={openAdminMenu === 'support'} onToggle={(isOpen) => setOpenAdminMenu(isOpen ? 'support' : null)}>
                <Dropdown.Toggle as={NestedDropdownToggle}>Supporto</Dropdown.Toggle>
                <Dropdown.Menu className="tootor-dropdown-menu">
                  <DropdownItemLink to={'/admin/users'} label={'Gestione utenti'} />
                  <DropdownItemLink to={'/admin/tickets'} label={'Tickets'} />
                  <DropdownItemLink to={'/admin/reports'} label={'Segnalazioni video'} />
                  <DropdownItemLink to={'/admin/feedbacks'} label={'Feedbacks'} />
                </Dropdown.Menu>
              </Dropdown>

              <Dropdown drop={'left'} show={openAdminMenu === 'content'} onToggle={(isOpen) => setOpenAdminMenu(isOpen ? 'content' : null)}>
                <Dropdown.Toggle as={NestedDropdownToggle}>Contenuti</Dropdown.Toggle>
                <Dropdown.Menu className="tootor-dropdown-menu">
                  <DropdownItemLink to={'/admin/content'} label={'Gestione contenuti'} />
                  <DropdownItemLink to={'/admin/upload'} label={'Upload'} />
                  <DropdownItemLink to={'/admin/publish'} label={'Pubblica video'} />
                  <DropdownItemLink to={'/admin/replace'} label={'Sostituisci video'} />
                  <DropdownItemLink to={'/admin/coming-soon'} label={'Aggiungi Coming Soon'} />
                  <DropdownItemLink to={'/admin/stats'} label={'Statistiche'} />
                  <DropdownItemLink to={'/admin/companies'} label={'Gestione aziende'} />
                  <DropdownItemLink to={'/admin/live'} label={'Gestione Live'} />
                </Dropdown.Menu>
              </Dropdown>

              <Dropdown drop={'left'} show={openAdminMenu === 'system'} onToggle={(isOpen) => setOpenAdminMenu(isOpen ? 'system' : null)}>
                <Dropdown.Toggle as={NestedDropdownToggle}>Sistema</Dropdown.Toggle>
                <Dropdown.Menu className="tootor-dropdown-menu">
                  <DropdownItemLink to={'/admin/cache'} label={'Gestione cache'} />
                  <DropdownItemLink to={'/admin/realtime'} label={'RWS'} beta />
                  <DropdownItemLink to={'/admin/logs'} label={'Logs'} />
                  <DropdownItemLink
                    to={'/admin/lab'}
                    label={'Lab'}
                    append={
                      <Badge variant={Object.values(userDataFmt?.experiments || {}).filter(Boolean).length ? 'success' : 'secondary'}>
                        {Object.values(userDataFmt?.experiments || {}).filter(Boolean).length}
                      </Badge>
                    }
                  />
                </Dropdown.Menu>
              </Dropdown>

              <Dropdown drop={'left'} show={openAdminMenu === 'security'} onToggle={(isOpen) => setOpenAdminMenu(isOpen ? 'security' : null)}>
                <Dropdown.Toggle as={NestedDropdownToggle}>Sicurezza</Dropdown.Toggle>
                <Dropdown.Menu className="tootor-dropdown-menu">
                  <DropdownItemLink to={'/admin/2fa'} label={'Autenticazione a due fattori'} />
                </Dropdown.Menu>
              </Dropdown>

              <Dropdown drop={'left'} show={openAdminMenu === 'external-stats'} onToggle={(isOpen) => setOpenAdminMenu(isOpen ? 'external-stats' : null)}>
                <Dropdown.Toggle as={NestedDropdownToggle}>Statistiche esterne</Dropdown.Toggle>
                <Dropdown.Menu className="tootor-dropdown-menu">
                  <DropdownItemLink to={'/admin/stripe-dashboard'} label={'Stripe'} beta />
                </Dropdown.Menu>
              </Dropdown>
            </>
          )}
          {userClaims.flow == 'sicuramente' && (
            <>
              <Dropdown.Divider />
              <Dropdown.Header>Sicuramente</Dropdown.Header>
              <DropdownItemLink to={'/flow/sicuramente'} label={'Gestione utenti'} />
            </>
          )}
          <Dropdown.Divider />
          {!!staticData.SHOW_FEEDBACK_MENU && !isNative && <DropdownItemLink onClick={handleFeedback} label={'Feedback'} />}
          <DropdownItemLink to={'/help'} label={t('labels.helpCenter')} />
          <DropdownItemLink onClick={handleLogout} label={t('labels.logout')} />
          {!!userDataFmt?.alias && (
            <>
              <Dropdown.Divider />
              <Dropdown.Header>
                {t('labels.account')}: {userDataFmt?.alias}
              </Dropdown.Header>
            </>
          )}
        </Dropdown.Menu>
      </Dropdown>

      <Feedback show={feedbackModalOpen} onHide={() => setFeedbackModalOpen(false)} />
    </>
  );
};

export { ProfileCircleComponent as ProfileCircle };

const GravatarDropdownToggle = forwardRef(({ children, onClick, size = 35 }: any, _) => {
  const [_url, setUrl] = useState(children.user?.photoURL);

  return (
    <Gravatar email={children.user?.email ?? ''} options={{ default: 'mp', size }}>
      {(url) => (
        <div
          className={`${styles.dropdownToggle} ${children.ENABLE_GAMING ? `${styles.gamingBox} p-0 rounded d-flex align-items-center py-md-2 px-md-3` : ''}`}
          onClick={(e) => {
            e.preventDefault();
            onClick(e);
          }}
        >
          <Image
            src={_url ?? url}
            onError={() => setUrl(url)}
            style={{
              width: `${size}px`,
              height: `${size}px`,
              backgroundColor: generateColorFromArbitraryString(children.user?.email ?? ''),
              // outline: '2px solid #ffc107',
            }}
            roundedCircle
          />
        </div>
      )}
    </Gravatar>
  );
});
